import { createGlobalState } from '@vueuse/core';
import { ref, computed } from 'vue';
import { campaignsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import type { MessageRulesResponse } from '~/types/messageRules.types';
import { useToasted } from '~/composables/useToasted';

interface MessageRulesContext {
    groupId?: string;
    orgUnitId?: number;
}

export const useMessageRules = createGlobalState(() => {
    const teamId = Spark.state.currentTeam.id;
    const messageRules = ref<MessageRulesResponse | null>(null);
    const loading = ref<boolean>(false);
    const error = ref<string>('');
    const toasted = useToasted();

    const getMessageRules = async (context: MessageRulesContext): Promise<void> => {
        const { groupId, orgUnitId } = context;

        if (!groupId && !orgUnitId) {
            return;
        }

        loading.value = true;
        error.value = '';

        try {
            const response = groupId
                ? await campaignsClient.getGroupMessageRules(teamId, groupId)
                : await campaignsClient.getOrgUnitMessageRules(teamId, orgUnitId);
            messageRules.value = response.data;
        } catch (e) {
            error.value = catchRequestError(e);
            toasted.error(error.value);
        } finally {
            loading.value = false;
        }
    };

    const marketingAllowed = computed(() => {
        if (!messageRules.value) {
            return true;
        }
        return messageRules.value.marketing_rules?.sending_allowed !== 'not_permitted';
    });

    const transactionalAllowed = computed(() => {
        if (!messageRules.value) {
            return true;
        }
        return messageRules.value.transactional_rules?.sending_allowed !== 'not_permitted';
    });

    const isTypeRestricted = (type: 'marketing' | 'transactional') => {
        if (!messageRules.value) {
            return false;
        }

        return type === 'marketing'
            ? messageRules.value.marketing_rules?.sending_allowed === 'restricted'
            : messageRules.value.transactional_rules?.sending_allowed === 'restricted';
    };

    const shouldBlockImages = (type: 'marketing' | 'transactional') => {
        if (!messageRules.value) {
            return false;
        }

        if (type === 'marketing') {
            return Boolean(messageRules.value.marketing_rules?.restrictions?.block_images);
        } else if (type === 'transactional') {
            return Boolean(messageRules.value.transactional_rules?.restrictions?.block_images);
        }

        return true;
    };

    const shouldBlockFiles = (type: 'marketing' | 'transactional') => {
        if (!messageRules.value) {
            return false;
        }

        if (type === 'marketing') {
            return Boolean(messageRules.value.marketing_rules?.restrictions?.block_files);
        } else if (type === 'transactional') {
            return Boolean(messageRules.value.transactional_rules?.restrictions?.block_files);
        }

        return true;
    };

    const lockedSnippetsEnabled = (type: 'marketing' | 'transactional') => {
        if (!messageRules.value || type === 'transactional') {
            return false;
        }

        return Boolean(messageRules.value.marketing_rules?.restrictions?.locked_snippets);
    };

    return {
        messageRules,
        loading,
        error,
        getMessageRules,
        marketingAllowed,
        transactionalAllowed,
        isTypeRestricted,

        shouldBlockImages,
        shouldBlockFiles,

        lockedSnippetsEnabled,
    };
});
